import type {
  IssueSummary,
  FailureDiagnostic,
  ConsoleLogEntry,
  NetworkRequestEntry,
} from '@chatsdet/types/issues';
import { formatDistanceToNow } from 'date-fns';
import { API_URL } from '../../../config';
import { CATEGORY_LABELS } from './diagnosticDisplay';

type FormatParams = {
  issue: IssueSummary;
  consoleErrors: ConsoleLogEntry[];
  networkErrors: NetworkRequestEntry[];
};

function formatRelative(value: string | null): string {
  if (!value) return '—';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return value;
  }
}

function formatStatusLabel(status: string): string {
  switch (status) {
    case 'open':
      return 'Open';
    case 'closed':
      return 'Closed';
    case 'not_a_bug':
      return 'Not a Bug';
    default:
      return status;
  }
}

function formatSeverity(severity: string): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

export function formatIssueAsMarkdown({ issue, consoleErrors, networkErrors }: FormatParams): string {
  const sections: string[] = [];
  const occ = issue.latestOccurrence;

  // Title
  sections.push(`# ${issue.title}`);

  // Metadata line
  const meta: string[] = [];
  meta.push(`**Status:** ${formatStatusLabel(issue.status)}`);
  meta.push(`**Severity:** ${formatSeverity(issue.severity)}`);
  meta.push(`**Category:** ${issue.category.label}`);
  sections.push(meta.join(' | '));

  if (occ?.primaryUrl) {
    sections.push(`**URL:** ${occ.primaryUrl}`);
  }

  const timeParts: string[] = [];
  timeParts.push(`**First seen:** ${formatRelative(issue.firstSeenAt)}`);
  timeParts.push(`**Last seen:** ${formatRelative(issue.lastSeenAt)}`);
  timeParts.push(`**Occurrences:** ${issue.occurrenceCount}`);
  sections.push(timeParts.join(' | '));

  // Summary
  const summary = occ?.ticketUserFacingSummary?.trim();
  if (summary) {
    sections.push(`## Summary\n${summary}`);
  }

  // Reproduction Steps
  const reproSteps = occ?.ticketReproSteps?.trim();
  if (reproSteps) {
    sections.push(`## Reproduction Steps\n${reproSteps}`);
  }

  // Diagnostic Analysis
  const diagnostic = occ?.diagnosticJson as FailureDiagnostic | null;
  if (diagnostic) {
    const diagParts: string[] = [];
    const categoryLabel = CATEGORY_LABELS[diagnostic.category];
    diagParts.push(`## Diagnostic Analysis`);
    diagParts.push(`**Category:** ${categoryLabel}`);
    diagParts.push(`**${diagnostic.title}**`);
    if (diagnostic.failurePoints && diagnostic.failurePoints.length > 0) {
      diagParts.push(diagnostic.failurePoints.map((p) => `- ${p}`).join('\n'));
    } else if (diagnostic.analysis) {
      diagParts.push(diagnostic.analysis);
    }

    if (diagnostic.smokingGun) {
      const gun = diagnostic.smokingGun;
      const gunLines = [`> **Key Evidence:** ${gun.description}`];
      if (gun.detail) {
        gunLines.push(`> \`${gun.detail}\``);
      }
      diagParts.push(gunLines.join('\n'));
    }

    diagParts.push(`**Recommendation:** ${diagnostic.recommendation}`);
    if (diagnostic.openQuestions && diagnostic.openQuestions.length > 0) {
      diagParts.push(
        `**Open questions:**\n${diagnostic.openQuestions.map((q) => `- ${q}`).join('\n')}`,
      );
    }
    sections.push(diagParts.join('\n\n'));
  }

  // Console Errors
  if (consoleErrors.length > 0) {
    const logLines = consoleErrors.map((entry) => {
      const loc = entry.location ? ` (${entry.location})` : '';
      return `[${entry.type}] ${entry.text}${loc}`;
    });
    sections.push(`## Console Errors\n\`\`\`\n${logLines.join('\n')}\n\`\`\``);
  }

  // Network Errors
  if (networkErrors.length > 0) {
    const rows = networkErrors.map((entry) => {
      const status =
        entry.status === null || entry.status === 0 ? '0 (failed)' : String(entry.status);
      const duration =
        entry.durationMs !== null && entry.durationMs !== undefined ? `${entry.durationMs}ms` : '—';
      return `| ${entry.method} | ${entry.url} | ${status} | ${duration} |`;
    });
    sections.push(
      `## Network Errors\n| Method | URL | Status | Duration |\n|--------|-----|--------|----------|\n${rows.join('\n')}`
    );
  }

  return sections.join('\n\n');
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json();
    return (json.data ?? json) as T;
  } catch {
    return null;
  }
}

type CopyParams = {
  issue: IssueSummary;
};

export async function copyIssueAsMarkdown({ issue }: CopyParams): Promise<void> {
  const occ = issue.latestOccurrence;

  let consoleErrors: ConsoleLogEntry[] = [];
  let networkErrors: NetworkRequestEntry[] = [];

  if (occ) {
    const [consoleLogs, networkRequests] = await Promise.all([
      fetchJson<ConsoleLogEntry[]>(
        `${API_URL}/v2/issues/${issue.id}/occurrences/${occ.id}/console-logs`
      ),
      fetchJson<NetworkRequestEntry[]>(
        `${API_URL}/v2/issues/${issue.id}/occurrences/${occ.id}/network-requests`
      ),
    ]);

    if (consoleLogs) {
      consoleErrors = consoleLogs.filter((e) => e.type === 'error');
    }

    if (networkRequests) {
      networkErrors = networkRequests.filter(
        (e) => e.status === null || e.status === 0 || e.status >= 400
      );
    }

    // Fallback to legacy fields if structured data unavailable
    if (consoleErrors.length === 0 && occ.consoleLogExcerpt?.trim()) {
      consoleErrors = [
        {
          type: 'error',
          text: occ.consoleLogExcerpt.trim(),
          timestamp: null,
          location: null,
        },
      ];
    }

    if (networkErrors.length === 0 && occ.networkFailureUrl) {
      networkErrors = [
        {
          url: occ.networkFailureUrl,
          method: 'GET',
          status: occ.networkFailureStatus,
          durationMs: null,
          requestBody: null,
          responseBody: null,
          requestHeaders: null,
          responseHeaders: null,
          contentType: null,
        },
      ];
    }
  }

  const markdown = formatIssueAsMarkdown({ issue, consoleErrors, networkErrors });
  await navigator.clipboard.writeText(markdown);
}
